// src/components/PreviewErrorBoundary.js
import React, { useState } from "react";
import PropTypes from "prop-types";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import { Text } from "@base";
import {
  clearPreviewSession,
  isInPreviewMode,
  PREVIEW_PARAM_KEYS,
} from "../utils/previewParams";

/* ------------------------------------------------------------------ */
/*  Functional wrapper – catches errors via the inner class           */
/* ------------------------------------------------------------------ */
function PreviewErrorBoundary({
  children = null,
  initialError = null,
  initialMessage = "",
}) {
  const [err, setErr] = useState(initialError);
  const [msg, setMsg] = useState(initialMessage);
  const navigate = useNavigate();

  if (err) {
    const statusCode = err?.error?.networkError?.statusCode;
    const isAuthError = statusCode === 400 || statusCode === 401 || statusCode === 403;
    const inPreview = isInPreviewMode();

    const displayMessage = isAuthError
      ? `Your preview session may have expired (HTTP ${statusCode}). Click "Exit Preview" to clear your session, then re-open the preview from CraftCMS.`
      : msg || "Something went wrong while building this manual or topic.";

    const handleExitPreview = () => {
      clearPreviewSession();
      const url = new URL(window.location.href);
      PREVIEW_PARAM_KEYS.forEach(k => url.searchParams.delete(k));
      window.location.href = url.toString();
    };

    return (
      <Box
        sx={{
          bgcolor: "#f5f5f5",
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Paper elevation={2} sx={{ p: 4, maxWidth: 640 }}>
          <Stack spacing={3}>
            <Text tag="h2" options={{ size: "text-xl" }}>
              🤕 Preview failed to render
            </Text>

            <Text tag="p" className="text-gray-3 whitespace-pre-wrap">
              {displayMessage}
            </Text>

            <Box
              sx={{
                maxHeight: 240,
                overflow: "auto",
                bgcolor: "#fee2e2",
                p: 2,
                borderRadius: 1,
                fontFamily: "monospace",
                fontSize: 14,
                whiteSpace: "pre-wrap",
              }}
            >
              {String(err.error)}
            </Box>

            <Stack direction="row" spacing={2}>
              {inPreview && (
                <Button variant="contained" color="warning" onClick={handleExitPreview}>
                  Exit Preview
                </Button>
              )}
              <Button variant="contained" onClick={() => navigate("/previews")}>
                Back to selector
              </Button>
              <Button variant="outlined" onClick={() => setErr(null)}>
                Try again
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Box>
    );
  }

  return <ErrorCatcher onError={setErr}>{children}</ErrorCatcher>;
}

PreviewErrorBoundary.propTypes = {
  children: PropTypes.node,
  initialError: PropTypes.object,
  initialMessage: PropTypes.string,
};

/* ------------------------------------------------------------------ */
/*  Class component so we can use componentDidCatch                   */
/* ------------------------------------------------------------------ */
class ErrorCatcher extends React.Component {
  componentDidCatch(error, info) {
    const { onError } = this.props; // ← destructuring-assignment
    onError({ error, info });
  }

  render() {
    const { children } = this.props; // ← destructuring-assignment
    return children;
  }
}

ErrorCatcher.propTypes = {
  children: PropTypes.node.isRequired,
  onError: PropTypes.func.isRequired,
};

export default PreviewErrorBoundary;
